/**
 * 来場者セッション保存サービス
 * ローカル保存された来場者認証情報を管理します
 */

import webStorage from '../../../shared/services/webStorage';
import { AppError } from '../../../shared/utils/errorHandling';
import { logWarn } from '../../../shared/utils/logger';
import { validateRequiredString } from '../../../shared/utils/validation';
import { clearDisplayedExchangeQr } from './visitorExchangeQrStore';

/** 来場者セッション保存キー */
const VISITOR_SESSION_STORAGE_KEY = '@ikomasai/visitor_session';

/** セッション保存失敗エラーコード */
export const VISITOR_SESSION_SAVE_FAILED_CODE = 'VISITOR_SESSION_SAVE_FAILED';

/** セッション読込失敗エラーコード */
export const VISITOR_SESSION_LOAD_FAILED_CODE = 'VISITOR_SESSION_LOAD_FAILED';

/** セッション削除失敗エラーコード */
export const VISITOR_SESSION_CLEAR_FAILED_CODE = 'VISITOR_SESSION_CLEAR_FAILED';

/**
 * セッションペイロードを検証する
 * @param {Object} sessionPayload - セッション情報
 * @returns {void}
 * @throws {AppError} セッション形式が不正な場合
 */
const validateSessionPayload = (sessionPayload) => {
  /** visitorId検証 */
  const visitorIdValidation = validateRequiredString(sessionPayload?.visitorId, 'visitorId');

  if (!visitorIdValidation.isValid) {
    throw new AppError('visitorId が不正です。', {
      code: VISITOR_SESSION_SAVE_FAILED_CODE,
      statusCode: 400,
    });
  }

  /** nickname検証 */
  const nicknameValidation = validateRequiredString(sessionPayload?.nickname, 'nickname');

  if (!nicknameValidation.isValid) {
    throw new AppError('nickname が不正です。', {
      code: VISITOR_SESSION_SAVE_FAILED_CODE,
      statusCode: 400,
    });
  }

  /** sessionToken検証 */
  const tokenValidation = validateRequiredString(sessionPayload?.sessionToken, 'sessionToken');

  if (!tokenValidation.isValid) {
    throw new AppError('sessionToken が不正です。', {
      code: VISITOR_SESSION_SAVE_FAILED_CODE,
      statusCode: 400,
    });
  }

  /** sessionExpiresAt検証 */
  const expiresAtValidation = validateRequiredString(sessionPayload?.sessionExpiresAt, 'sessionExpiresAt');

  if (!expiresAtValidation.isValid) {
    throw new AppError('sessionExpiresAt が不正です。', {
      code: VISITOR_SESSION_SAVE_FAILED_CODE,
      statusCode: 400,
    });
  }
};

/**
 * 来場者セッション情報を保存する
 * @param {{ visitorId: string, nickname: string, sessionToken: string, sessionExpiresAt: string }} sessionPayload - 保存対象
 * @returns {Promise<void>}
 * @throws {AppError} 保存に失敗した場合
 */
export const saveVisitorSession = async (sessionPayload) => {
  validateSessionPayload(sessionPayload);

  try {
    /** 保存文字列 */
    const serializedPayload = JSON.stringify(sessionPayload);
    await webStorage.setItem(VISITOR_SESSION_STORAGE_KEY, serializedPayload);
  } catch (error) {
    throw new AppError('来場者セッション情報の保存に失敗しました。', {
      code: VISITOR_SESSION_SAVE_FAILED_CODE,
      statusCode: 500,
      details: {
        message: error instanceof Error ? error.message : String(error),
      },
      cause: error instanceof Error ? error : undefined,
    });
  }
};

/**
 * 保存済み来場者セッション情報を取得する
 * @returns {Promise<{
 * visitorId: string,
 * nickname: string,
 * sessionToken: string,
 * sessionExpiresAt: string,
 * } | null>} セッション情報
 * @throws {AppError} 読み込みに失敗した場合
 */
export const loadVisitorSession = async () => {
  try {
    /** 保存文字列 */
    const serializedPayload = await webStorage.getItem(VISITOR_SESSION_STORAGE_KEY);

    if (!serializedPayload) {
      return null;
    }

    try {
      /** 復元セッション */
      const sessionPayload = JSON.parse(serializedPayload);
      validateSessionPayload(sessionPayload);
      return sessionPayload;
    } catch (error) {
      // 破損データは復元せず削除して再ログインを促す
      logWarn('保存済み来場者セッション情報が破損していたため削除します。', {
        message: error instanceof Error ? error.message : String(error),
      });
      await webStorage.removeItem(VISITOR_SESSION_STORAGE_KEY);
      return null;
    }
  } catch (error) {
    throw new AppError('来場者セッション情報の読み込みに失敗しました。', {
      code: VISITOR_SESSION_LOAD_FAILED_CODE,
      statusCode: 500,
      details: {
        message: error instanceof Error ? error.message : String(error),
      },
      cause: error instanceof Error ? error : undefined,
    });
  }
};

/**
 * 保存済み来場者セッション情報を削除する
 * 表示中の交換QRの保存もセッションに紐付くため、あわせて消す。
 * @returns {Promise<void>}
 * @throws {AppError} 削除に失敗した場合
 */
export const clearVisitorSession = async () => {
  try {
    await webStorage.removeItem(VISITOR_SESSION_STORAGE_KEY);
    await clearDisplayedExchangeQr();
  } catch (error) {
    throw new AppError('来場者セッション情報の削除に失敗しました。', {
      code: VISITOR_SESSION_CLEAR_FAILED_CODE,
      statusCode: 500,
      details: {
        message: error instanceof Error ? error.message : String(error),
      },
      cause: error instanceof Error ? error : undefined,
    });
  }
};
